

import { useState, useRef, useEffect } from "react";
import { Upload, X } from "lucide-react";
import imageCompression from "browser-image-compression";
import { imageApi, MAIN_URL } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { getImageUrl } from "@/lib/utils";

interface ImageUploadFieldProps {
  label: string;
  value?: string;
  onChange: (url: string) => void;
  type: "logo" | "favicon" | "gallery" | "pwa";
  settingKey: string;
  compact?: boolean;
}

export default function ImageUploadField({
  label,
  value,
  onChange,
  type,
  settingKey,
  compact = false,
}: ImageUploadFieldProps) {
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compressionInfo, setCompressionInfo] = useState<{
    original: number;
    compressed: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Cleanup blob URLs to prevent memory leaks and DOM issues
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Compress and convert image to WebP using browser-image-compression
  const compressImage = async (file: File): Promise<File> => {
    // Determine output format and settings based on type
    const isFavicon = type === 'favicon';
    const isPWA = type === 'pwa';
    const maxWidthOrHeight = isFavicon ? 180 : isPWA ? 512 : 2048;
    const maxSizeMB = isFavicon ? 0.5 : isPWA ? 1 : 1;
    
    const options = {
      maxSizeMB: maxSizeMB,
      maxWidthOrHeight: maxWidthOrHeight,
      useWebWorker: true,
      fileType: 'image/webp',
      initialQuality: 0.85,
      alwaysKeepResolution: false,
    };

    try {
      // Compress and convert to WebP
      const compressedBlob = await imageCompression(file, options);
      
      // Create new filename with .webp extension
      const originalName = file.name.replace(/\.[^/.]+$/, '');
      let webpFileName;
      if (isFavicon) {
        webpFileName = 'favicon.webp';
      } else if (isPWA) {
        webpFileName = `${originalName}.webp`;
      } else {
        webpFileName = `${originalName}.webp`;
      }
      
      // Create File object from Blob
      const compressedFile = new File([compressedBlob], webpFileName, { 
        type: 'image/webp',
        lastModified: Date.now()
      });
      
      setCompressionInfo({
        original: file.size,
        compressed: compressedFile.size,
      });
      
      console.log(`Compressed ${file.name} (${formatFileSize(file.size)}) to ${webpFileName} (${formatFileSize(compressedFile.size)})`);
      
      return compressedFile;
    } catch (error) {
      console.error("Compression error:", error);
      // Fallback: return original file if compression fails
      return file;
    }
  };

  const handleFileSelect = async (file: File | null) => {
    if (!file) return;

    // Validate file type
    if (!["image/jpeg", "image/png", "image/webp", "image/avif"].includes(file.type)) {
      showToast("Please upload a valid image (JPEG, PNG, WebP, or AVIF)", "error");
      return;
    }

    // Clean up previous blob URL if exists
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    // Create local preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    setUploading(true);
    try {
      // Compress image first
      const compressedFile = await compressImage(file);

      // Upload via imageApi which calls the Laravel backend directly
      const result = await imageApi.upload(compressedFile, type, settingKey);

      if (result && result.success) {
        onChange(result.data?.url || result.url);
        showToast("Image uploaded successfully!", "success");
      } else {
        showToast(result?.message || "Upload failed", "error");
      }
    } catch (error: any) {
      showToast(error.message || "Upload failed", "error");
      console.error("Image compression/upload error:", error);
    } finally {
      setUploading(false);
      // Clean up blob URL
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add("border-amber-400", "bg-amber-50");
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove("border-amber-400", "bg-amber-50");
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove("border-amber-400", "bg-amber-50");
    }

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    try {
      await imageApi.delete(value, settingKey);
      onChange("");
      // Clean up blob URL if it exists
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      showToast("Image removed", "success");
    } catch (error: any) {
      showToast(error.message || "Failed to remove image", "error");
    }
  };

  if (compact) {
    const imageUrl = previewUrl || getImageUrl(value);
    const hasValidImage = !!imageUrl;
    
    return (
      <div className="w-full">
        <div className="flex items-center gap-2">
          {hasValidImage ? (
            <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
              <img
                src={imageUrl}
                alt={label}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpolyline points="21 15 16 10 5 21"/%3E%3C/svg%3E';
                }}
              />
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <svg
                    className="animate-spin w-4 h-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
              {!uploading && (
                <button
                  onClick={handleRemove}
                  className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-12 h-12 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 hover:border-amber-400 hover:bg-amber-50 transition-colors flex items-center justify-center disabled:opacity-50"
            >
              <Upload className="w-4 h-4 text-stone-400" />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            onChange={(e) => handleInputChange(e)}
            disabled={uploading}
            className="hidden"
          />
          <span className="text-xs text-stone-500">
            {uploading ? "Uploading..." : value ? "Click to change" : "Click to upload"}
          </span>
        </div>
      </div>
    );
  }

  const fullImageUrl = previewUrl || getImageUrl(value);
  const hasValidFullImage = !!fullImageUrl;
  
  return (
    <div className="w-full">
      <label className="block text-xs font-medium text-stone-500 mb-2">
        {label}
      </label>
      <div
        ref={dropZoneRef}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors"
      >
        {hasValidFullImage ? (
          <div className="space-y-3">
            <img
              src={fullImageUrl}
              alt={label}
              className="w-24 h-24 mx-auto rounded-lg object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpolyline points="21 15 16 10 5 21"/%3E%3C/svg%3E';
              }}
            />
            <p className="text-sm font-medium text-stone-700">
              {uploading ? "Uploading..." : "Image selected"}
            </p>
            {uploading && (
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin w-5 h-5 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="text-sm text-stone-600">Uploading to server...</span>
              </div>
            )}
            {compressionInfo && !uploading && (
              <div className="text-xs text-stone-600 space-y-1">
                <p>Original: {formatFileSize(compressionInfo.original)}</p>
                <p>Compressed: {formatFileSize(compressionInfo.compressed)}</p>
                <p className="text-emerald-600 font-medium">
                  Saved: {formatFileSize(compressionInfo.original - compressionInfo.compressed)}
                </p>
              </div>
            )}
            {!uploading && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="text-xs text-rose-600 hover:text-rose-700 font-medium"
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="w-8 h-8 text-stone-400 mx-auto" />
            <div>
              <p className="text-sm font-medium text-stone-700">
                Drag & drop or click to upload {label.toLowerCase()}
              </p>
              <p className="text-xs text-stone-500 mt-1">PNG, JPG, WebP or AVIF</p>
            </div>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        onChange={(e) => handleInputChange(e)}
        disabled={uploading}
        className="hidden"
      />
    </div>
  );
}
